import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { firePageEnrichment } from '../lib/claudeApi';
import { API_BASE } from '../lib/config';
import FeedbackButton from '../components/FeedbackButton';
import { track } from '../lib/analytics';
import { checkAndRestoreActivityCache } from '../lib/claudeApi';

const TIER_COLORS = {
  iconic: '#7eb8c9',
  underrated: '#b8a0e8',
  hidden_gem: '#7ec49b',
};

const TIER_BG = {
  iconic: '#2a6b8a',
  underrated: '#3d2f6b',
  hidden_gem: '#1a5c3a',
};

const TIER_LABELS = {
  iconic: 'Iconic',
  underrated: 'Underrated',
  hidden_gem: 'Hidden Gem',
};

const TIER_ICONS = {
  iconic: '🏛️',
  underrated: '🧭',
  hidden_gem: '💎',
};

const PROFILES = [
  { name: 'The Pioneer', description: 'You consider a destination ruined the moment it appears on a top ten list. You want the place before the crowds find it. Discovery is not a preference, it is a requirement.' },
  { name: 'The Adventurer', description: 'You want to come home having seen something most people have never googled. Not just visited, but genuinely encountered. You use iconic destinations as orientation points, not destinations.' },
  { name: 'The Explorer', description: 'One anchor to orient yourself, everything else is discovery. You find the version of a place most visitors never reach because they did not know to look.' },
  { name: 'The Curious Traveler', description: 'Half familiar, half discovery. You come home with stories and recommendations nobody else at the dinner table has heard. You balance comfort with genuine curiosity.' },
  { name: 'The Selective Explorer', description: 'You appreciate the classics but always find that one place nobody else has been to. You do the research, go off script at least once, and it is always the best part of the trip.' },
  { name: 'The Classics', description: 'You know what you love and you love it for good reason. The iconic destinations deliver and you are here for every bit of it. You do the classics properly, which most people never do.' },
  { name: 'The Wanderluster', description: 'The destination almost does not matter. What matters is that it is somewhere new. You travel because you love to travel, full stop, and every new place is a good place.' },
];

const spinnerStyle = {
  width: '18px',
  height: '18px',
  border: '2px solid rgba(255,255,255,0.2)',
  borderTop: '2px solid #c9a84c',
  borderRadius: '50%',
  display: 'inline-block',
  animation: 'spin 0.8s linear infinite',
};

if (typeof document !== 'undefined' && !document.getElementById('carpe-spinner-style')) {
  const style = document.createElement('style');
  style.id = 'carpe-spinner-style';
  style.textContent = '@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}

function Results() {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [enrichTeasers, setEnrichTeasers] = useState({});
  const [enrichFacts, setEnrichFacts] = useState({});
  const [favorites, setFavorites] = useState(new Set());
  const [starred, setStarred] = useState(new Set());
  const [flaggedIndex, setFlaggedIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const destinationsRef = useRef(null);

  // Heart = history signal. All destinations hearted by default.
  // initHearts sets all ON locally and saves new ones to history (skips existing)
  function initHearts(dests) {
    const userId = sessionStorage.getItem('carpeUserId');
    if (!userId) {
      // Not logged in — heart everything locally
      setFavorites(new Set(dests.map(d => d.name)));
      return;
    }
    // Check history first — set hearts based on what's actually in history
    fetch(`/api/auth/history/destinations/${userId}?page=1&page_size=50`)
      .then(res => res.json())
      .then(data => {
        const inHistory = new Set(
          (data.destinations || []).map(d => d.destination_name)
        );
        // Heart only destinations that are in history
        const heartSet = new Set(
          dests.filter(d => inHistory.has(d.name)).map(d => d.name)
        );
        // Save new destinations to history and heart them too
        dests
          .filter(dest => !inHistory.has(dest.name))
          .forEach(dest => {
            heartSet.add(dest.name);
            fetch(`${API_BASE}/auth/history/destination/save`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                user_id: userId,
                destination_name: dest.name,
                country: dest.country || '',
                tier: dest.tier || '',
                match_reason: dest.match_reason || '',
              }),
            }).catch(() => {});
          });
        setFavorites(heartSet);
      })
      .catch(() => {
        // On error default to all hearted
        setFavorites(new Set(dests.map(d => d.name)));
      });
  }

  // Load profile and destinations on mount
  useEffect(() => {
    const stored = sessionStorage.getItem('carpeWonderProfile');
    if (!stored) { navigate('/'); return; }
    setProfile(JSON.parse(stored));

    // Check router state first (passed directly from Loading.jsx — no race condition)
    const navDests = location.state?.destinations || [];
    const savedDests = sessionStorage.getItem('carpeSavedDestinations');
    const dests = navDests.length === 5 ? navDests :
                  savedDests ? JSON.parse(savedDests) : [];
    if (dests.length === 0) {
      navigate('/loading');
      return;
    }
    
    setDestinations(dests);


    initHearts(dests);
    sessionStorage.removeItem('carpeReturningUser');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Load stars from backend (hearts come from history via initHearts)
  useEffect(() => {
    const userId = sessionStorage.getItem('carpeUserId');
    if (!userId) return;
    fetch(`/api/auth/favorites/${userId}`)
      .then(res => res.json())
      .then(data => {
        const starSet = new Set(
          (data.favorites || [])
            .filter(f => f.type === 'destination_star')
            .map(f => f.item_data.name)
        );
        setStarred(starSet);
      })
      .catch(() => {});
  }, []);

  // Poll for enrichment data from sessionStorage
  useEffect(() => {
    const checkEnrichment = () => {
      const teaserMap = JSON.parse(sessionStorage.getItem('carpeEnrichTeasers') || '{}');
      const factsMap = JSON.parse(sessionStorage.getItem('carpeEnrichFacts') || '{}');
      if (Object.keys(teaserMap).length > 0) setEnrichTeasers(teaserMap);
      if (Object.keys(factsMap).length > 0) setEnrichFacts(factsMap);
    };
    checkEnrichment();
    const interval = setInterval(checkEnrichment, 2000);
    return () => clearInterval(interval);
  }, []);
  
  // Heart toggle — adds/removes from history
  function handleHeartDestination(dest) {
    const userId = sessionStorage.getItem('carpeUserId');
    const isHearted = favorites.has(dest.name);

    // Update local state immediately
    setFavorites(prev => {
      const next = new Set(prev);
      if (isHearted) next.delete(dest.name);
      else next.add(dest.name);
      return next;
    });

    if (!userId) return;

    if (isHearted) {
      track('Destination Unhearted', { destination: dest.name, tier: dest.tier || '' });
      // Unheart — remove from history
      fetch(`${API_BASE}/auth/history/destination/remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          destination_name: dest.name,
        }),
      }).catch(() => {});
    } else {
      track('Destination Hearted', { destination: dest.name, tier: dest.tier || '' });
      // Re-heart — add back to history
      fetch(`${API_BASE}/auth/history/destination/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          destination_name: dest.name,
          country: dest.country || '',
          tier: dest.tier || '',
          match_reason: dest.match_reason || '',
        }),
      }).catch(() => {});
    }
  }

  // Star toggle — explicit save to favorites, subject to tier ceiling
  function handleStarDestination(dest) {
    const userId = sessionStorage.getItem('carpeUserId');
    if (!userId) return;
    fetch(`${API_BASE}/auth/favorites/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: userId,
        type: 'destination_star',
        item_data: { name: dest.name, country: dest.country, tier: dest.tier, match_reason: dest.match_reason },
      }),
    })
      .then(res => res.json())
      .then(data => {
        track(data.action === 'added' ? 'Destination Starred' : 'Destination Unstarred', {
          destination: dest.name,
          tier: dest.tier || '',
        });
        setStarred(prev => {
          const next = new Set(prev);
          if (data.action === 'added') next.add(dest.name);
          else next.delete(dest.name);
          return next;
        });
      })
      .catch(() => {});
  }

  function toggleExpand(index) {
    if (flaggedIndex !== null) setFlaggedIndex(null);
    setExpandedIndex(prev => prev === index ? null : index);
  }

  async function handleSelectDestination(dest) {
    sessionStorage.setItem('carpeDestination', JSON.stringify(dest));
    const prof = profile;
    sessionStorage.setItem('carpeWonderProfile', JSON.stringify(prof));
    const userId = sessionStorage.getItem('carpeUserId');
    if (userId) {
      const restored = await checkAndRestoreActivityCache(userId, dest.name, dest.event_date || null);
      if (restored) {
        sessionStorage.setItem('carpeActivitiesReferrer', '/results');
        navigate('/activities');
        return;
      }
    }
    const teasers = enrichTeasers[dest.name] || dest.teaser_activities || [];
    const facts = enrichFacts[dest.name] || {};
    const allFacts = [...(facts.profile_match_facts || []), ...(facts.profile_surprise_facts || [])];

    sessionStorage.setItem('carpeLoadingContext', JSON.stringify({
      type: 'activities',
      endpoint: '/recommendations/activities',
      payload: {
        destination: dest.name,
        country: dest.country,
        state_province: dest.state_province || null,
        profile: {
          profile: prof.profile,
          pace: prof.pace,
          activity_level: prof.activity_level,
          best_for_tags: prof.best_for_tags,
          social_vibe: prof.social_vibe,
          explorer_score: prof.explorer_score,
          activity_profile: prof.activity_profile,
          activity_dimensions: prof.activity_dimensions,
          activity_profile_description: prof.activity_profile_description,
        },
        micro_answers: JSON.parse(sessionStorage.getItem('carpeMicroAnswers') || '{}'),
        teaser_activities: teasers,
      },
      destination: '/activities',
      loadingMessage: 'Finding the best experiences in ' + dest.name + '...',
      destinationFacts: allFacts,
    }));
    sessionStorage.setItem('carpeActivitiesReferrer', '/results');
    navigate('/loading');
  }
  
  function handleContinueTheWonder() {
    const pool = JSON.parse(sessionStorage.getItem('carpePaginationPool') || '{}');
    const nextPageKey = `page${currentPage + 1}`;
    const nextDests = pool[nextPageKey];
    if (nextDests && nextDests.length > 0) {
      // Page already ready — display immediately
      setCurrentPage(prev => prev + 1);
      setDestinations(nextDests);
      setExpandedIndex(null);
      initHearts(nextDests);
      if (destinationsRef.current) {
        destinationsRef.current.scrollIntoView({ behavior: 'smooth' });
      }
      // Fire page after next in background
      firePageInBackground(currentPage + 2);
    } else {
      // Page not ready — fire enrichment now and display when done
      const ranked = JSON.parse(sessionStorage.getItem('carpeRankedPool') || '[]');
      const startIdx = currentPage * 5;
      const candidates = ranked.slice(startIdx, startIdx + 5);
      if (candidates.length === 0) return;
      const profileData = JSON.parse(sessionStorage.getItem('carpeWonderProfile') || '{}');
      const userId = sessionStorage.getItem('carpeUserId');
      firePageEnrichment(candidates, currentPage + 1, profileData, userId, (dests) => {
        setCurrentPage(prev => prev + 1);
        setDestinations(dests);
        setExpandedIndex(null);
        initHearts(dests);
        if (destinationsRef.current) {
          destinationsRef.current.scrollIntoView({ behavior: 'smooth' });
        }
        // Fire page after next in background
        firePageInBackground(currentPage + 2);
      });
    }
  }

  function firePageInBackground(pageNum) {
    const ranked = JSON.parse(sessionStorage.getItem('carpeRankedPool') || '[]');
    const pageKey = `page${pageNum}`;
    const pool = JSON.parse(sessionStorage.getItem('carpePaginationPool') || '{}');
    if (pool[pageKey]) return;
    const startIdx = (pageNum - 1) * 5;
    const candidates = ranked.slice(startIdx, startIdx + 5);
    if (candidates.length === 0) return;
    const profileData = JSON.parse(sessionStorage.getItem('carpeWonderProfile') || '{}');
    const userId = sessionStorage.getItem('carpeUserId');
    firePageEnrichment(candidates, pageNum, profileData, userId, null);
  }

  function handleGoBack() {
    if (currentPage <= 1) return;
    const pool = JSON.parse(sessionStorage.getItem('carpePaginationPool') || '{}');
    const prevPageKey = `page${currentPage - 1}`;
    const prevDests = pool[prevPageKey];
    if (!prevDests || prevDests.length === 0) return;
    setCurrentPage(prev => prev - 1);
    setDestinations(prevDests);
    setExpandedIndex(null);
    initHearts(prevDests);
    if (destinationsRef.current) {
      destinationsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }


  function getAvailablePages() {
    const pool = JSON.parse(sessionStorage.getItem('carpePaginationPool') || '{}');
    return Object.keys(pool).length + 1;
  }

  if (!profile || destinations.length === 0) return null;

  return (
    <div style={styles.page}>
      <FeedbackButton />
      <div style={styles.container}>
        <div style={styles.header}>
          {sessionStorage.getItem('carpeUserId') ? (
            <button style={styles.retakeBtn} onClick={() => navigate('/welcome')}>← Welcome</button>
          ) : (
            <div style={styles.logo}>Carpe Wonder™</div>
          )}
          <div style={styles.headerActions}>
            <button style={styles.retakeBtn} onClick={() => navigate('/quiz')}>Retake quiz</button>
            {sessionStorage.getItem('carpeUserId') && (
              <button style={styles.logoutBtn} onClick={() => { sessionStorage.clear(); navigate('/'); }}>Sign out</button>
            )}
          </div>
        </div>

        {profile && (
          <div style={styles.profileBanner}>
            <div style={styles.profileName}>{profile.profile}</div>
            <div style={styles.profileDesc}>
              {PROFILES.find(p => p.name === profile.profile)?.description || ''}
            </div>
          </div>
        )}

        <h1 style={styles.sectionTitle}>Your destinations</h1>
        <p style={styles.sectionSubtitle}>
          Tap a destination to peek at activities. Take your time, all five were chosen for you specifically.
        </p>
        <div style={styles.iconNote}>
          <div style={styles.iconNoteRow}>
            <span style={styles.iconNoteIcon}>🤍</span>
            <span>Heart a destination to add it to your history</span>
          </div>
          <div style={styles.iconNoteRow}>
            <span style={{...styles.iconNoteIcon, fontSize: '18px'}}>☆</span>
            <span>Star it to save it to your favorites</span>
          </div>
        </div>

        <div ref={destinationsRef}>
        <div style={styles.cardList}>
          {destinations.map((dest, index) => {
            const isExpanded = expandedIndex === index;
            const tierColor = TIER_COLORS[dest.tier] || '#c9a84c';
            return (
              <div key={index} style={{ ...styles.card, borderColor: isExpanded ? tierColor : 'rgba(255,255,255,0.08)' }}>
                <div style={styles.cardHeader} onClick={() => toggleExpand(index)}>
                  <div style={styles.cardLeft}>
                    <div style={styles.cardRank}>0{index + 1}</div>
                    <div>
                      <div style={styles.cardName}>
                        {dest.name}
                        {dest.state_province && <span style={styles.cardState}>, {dest.state_province}</span>}
                      </div>
                      <div style={styles.cardCountry}>{dest.country}</div>
                    </div>
                  </div>
                  <div style={styles.cardRight}>
                    <div style={{ ...styles.tierBadge, color: tierColor, background: TIER_BG[dest.tier] || 'transparent', border: 'none' }}>
                      {TIER_ICONS[dest.tier]} {TIER_LABELS[dest.tier]}
                    </div>
                    <div style={styles.cardActions}>
                      <button style={styles.heartBtn} onClick={e => { e.stopPropagation(); handleHeartDestination(dest); }}>
                        {favorites.has(dest.name) ? '❤️' : '🤍'}
                      </button>
                      <button style={styles.starBtn} onClick={e => { e.stopPropagation(); handleStarDestination(dest); }}>
                        {starred.has(dest.name) ? '⭐' : '☆'}
                      </button>
                    </div>
                    <div style={styles.chevron}>{isExpanded ? '↑' : '↓'}</div>
                  </div>
                </div>

                {isExpanded && (
                  <div style={styles.cardBody}>
                    <p style={styles.matchReason}>{dest.match_reason}</p>
                    {enrichTeasers[dest.name] ? (
                      <div style={styles.teaserCard}>
                        <div style={styles.teaserName}>{enrichTeasers[dest.name][0]?.name}</div>
                        <div style={styles.teaserDesc}>{enrichTeasers[dest.name][0]?.description}</div>
                        <div style={styles.teaserTip}>💡 {enrichTeasers[dest.name][0]?.local_tip}</div>
                      </div>
                    ) : (
                      <div style={styles.teaserLoading}>
                        <div style={spinnerStyle} />
                        <div style={styles.teaserLoadingText}>Finding a taste of {dest.name}...</div>
                      </div>
                    )}
                    <button style={styles.activitiesBtn} onClick={() => handleSelectDestination(dest)}>
                      Seize {dest.name} activities →
                    </button>
                    <div style={styles.affiliateLinks}>
                      <a
                        href={`https://www.expedia.com/Hotel-Search?destination=${encodeURIComponent(dest.name + ', ' + dest.country)}&affcid=CuC5c5o`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={styles.affiliateLink}
                      >
                        🏨 Find hotels in {dest.name}
                      </a>
                      <a
                        href="https://safetywing.com/nomad-insurance/?referenceID=26549148&utm_source=26549148&utm_medium=Ambassador"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={styles.affiliateLink}
                      >
                        🛡️ Protect your trip with travel insurance
                      </a>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </div>

        {/* Pagination */}
        {(() => {
          const totalPages = getAvailablePages();
          const hasMore = currentPage < totalPages;
          return (
            <div style={styles.pagination}>
              {/* Dot indicators */}
              <div style={styles.dotRow}>
                {Array.from({ length: totalPages }, (_, i) => (
                  <div
                    key={i}
                    style={{
                      ...styles.dot,
                      background: i + 1 === currentPage ? 'var(--gold)' : 'rgba(255,255,255,0.2)',
                      width: i + 1 === currentPage ? '20px' : '8px',
                    }}
                  />
                ))}
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                {currentPage > 1 && (
                  <button style={{...styles.continueBtn, background: 'transparent', border: '1px solid var(--gold)', color: 'var(--gold)'}} onClick={handleGoBack}>
                    ← Back
                  </button>
                )}
                {hasMore && (
                  <button style={styles.continueBtn} onClick={handleContinueTheWonder}>
                    Continue the Wonder →
                  </button>
                )}
              </div>
              {!hasMore && currentPage > 1 && (
                <div style={styles.endOfPool}>
                  You've explored all your destinations for this session.
                </div>
              )}
            </div>
          );
        })()}

      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#0d0d1f', padding: '0 0 48px' },
  container: { maxWidth: '480px', margin: '0 auto', padding: '0 20px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '28px 0 20px' },
  logo: { fontSize: '14px', color: 'var(--gold)', letterSpacing: '0.14em', textTransform: 'uppercase' },
  retakeBtn: { background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: '13px', padding: '4px 0', cursor: 'pointer', minHeight: '44px' },
  headerActions: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' },
  logoutBtn: { background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.45)', fontSize: '12px', padding: '4px 0', cursor: 'pointer' },
  profileBanner: { border: '0.5px solid rgba(201,168,76,0.25)', borderRadius: '12px', padding: '14px 16px', marginBottom: '28px', background: 'rgba(201,168,76,0.04)' },
  profileName: { fontSize: '16px', fontWeight: '500', color: 'var(--gold)', marginBottom: '4px' },
  profileDesc: { fontSize: '14px', color: 'rgba(255,255,255,0.65)', lineHeight: '1.7', marginTop: '6px' },
  sectionTitle: { fontSize: '22px', fontWeight: '500', color: '#fff', marginBottom: '8px' },
  sectionSubtitle: { fontSize: '14px', color: 'rgba(255,255,255,0.65)', lineHeight: '1.6', marginBottom: '12px' },
  iconNote: { display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginBottom: '20px', padding: '10px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px', border: '0.5px solid rgba(255,255,255,0.08)' },
  iconNoteRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  iconNoteIcon: { fontSize: '16px', width: '20px', textAlign: 'center', flexShrink: 0 },
  cardList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  pagination: { marginTop: '32px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' },
  dotRow: { display: 'flex', gap: '6px', alignItems: 'center' },
  dot: { height: '8px', borderRadius: '4px', transition: 'all 0.3s ease' },
  continueBtn: { width: '100%', background: 'transparent', border: '1px solid var(--gold)', borderRadius: '24px', color: 'var(--gold)', padding: '14px', fontSize: '15px', fontWeight: '500', cursor: 'pointer', minHeight: '44px', letterSpacing: '0.02em' },
  endOfPool: { fontSize: '13px', color: 'rgba(255,255,255,0.4)', textAlign: 'center', fontStyle: 'italic' },
  card: { border: '0.5px solid', borderRadius: '14px', background: '#1a1a35', overflow: 'visible', transition: 'border-color 0.3s ease' },
  cardHeader: { width: '100%', background: 'transparent', border: 'none', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', gap: '12px', minHeight: '44px' },
  cardLeft: { display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 },
  cardRank: { fontSize: '12px', color: 'rgba(255,255,255,0.35)', fontWeight: '500', minWidth: '20px' },
  cardName: { fontSize: '15px', fontWeight: '500', color: '#fff', textAlign: 'left' },
  cardState: { fontWeight: '400', color: 'rgba(255,255,255,0.6)' },
  cardCountry: { fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginTop: '2px', textAlign: 'left' },
  cardRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', flexShrink: 0 },
  tierBadge: { fontSize: '12px', borderRadius: '20px', padding: '4px 10px', letterSpacing: '0.05em' },
  chevron: { fontSize: '14px', color: 'rgba(255,255,255,0.5)' },
  cardActions: { display: 'flex', gap: '4px', alignItems: 'center' },
  heartBtn: { background: 'none', border: 'none', fontSize: '16px', cursor: 'pointer', padding: '0 4px', lineHeight: 1 },
  starBtn: { background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', padding: '0 4px', lineHeight: 1, color: 'rgba(255,255,255,0.6)' },
  cardBody: { padding: '0 16px 16px', borderTop: '0.5px solid rgba(255,255,255,0.06)', paddingTop: '14px' },
  matchReason: { fontSize: '14px', color: 'rgba(255,255,255,0.75)', lineHeight: '1.7', marginBottom: '16px' },
  activitiesBtn: { width: '100%', background: 'transparent', border: '1px solid var(--gold)', borderRadius: '24px', color: 'var(--gold)', padding: '12px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', minHeight: '44px' },
  affiliateLinks: { display: 'flex', flexDirection: 'column', gap: '8px', margin: '12px 0' },
  affiliateLink: { fontSize: '13px', color: 'rgba(255,255,255,0.5)', textDecoration: 'none', padding: '4px 0' },
  teaserLoading: { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 0', marginBottom: '12px' },
  teaserLoadingText: { fontSize: '13px', color: 'rgba(255,255,255,0.45)', fontStyle: 'italic' },
  teaserCard: { background: 'rgba(255,255,255,0.03)', border: '0.5px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' },
  teaserName: { fontSize: '14px', fontWeight: '500', color: 'var(--gold)' },
  teaserDesc: { fontSize: '13px', color: 'rgba(255,255,255,0.65)', lineHeight: '1.6' },
  teaserTip: { fontSize: '12px', color: 'rgba(255,255,255,0.45)', lineHeight: '1.5', fontStyle: 'italic' },
};

export default Results;